import { Controller } from "@hotwired/stimulus";

const SOURCE_SELECTOR = "turbo-cable-stream-source";
const LIVE_SELECTOR = "[data-live-channel]";
const GRACE_MS = 4000; // wait this long before calling a not-yet-connected page a "problem"
const RETRY_MS = 15000; // while in "problem" state, retry the connection this often

// Connects to data-controller="connection-status"
// Renders a discreet dot reflecting the page's Turbo/ActionCable stream health.
// An element marked data-live-channel (the Mapas board) heals itself and
// mirrors its state in data-live-status: it counts as a stream here, and while
// one is on the page this controller never revisits it.
export default class extends Controller {
	static targets = ["dot", "label"];
	static values = {
		labels: Object, // { connected, connecting, problem, offline }
	};

	static tally(streams, channels) {
		return {
			sourceCount: streams.length + channels.length,
			connectedCount: streams.filter((s) => s.hasAttribute("connected")).length + channels.filter((c) => c.dataset.liveStatus === "live").length,
		};
	}

	static computeState({ sourceCount, connectedCount, online }) {
		if (!online) return "problem";
		if (sourceCount === 0) return "idle"; // no live streams on this page
		if (connectedCount >= sourceCount) return "connected";
		return "connecting";
	}

	// turbo-rails sets/clears `connected` itself and never re-renders it server-side, so
	// idiomorph must not touch it — it's not in TurboCableStreamSourceElement's
	// observedAttributes, so a wrongly stripped value would never get fixed by the element itself.
	static shouldPreventMorph({ tagName, attributeName }) {
		return tagName === "TURBO-CABLE-STREAM-SOURCE" && attributeName === "connected";
	}

	// A "problem" caused by the browser being offline gets its own copy; any other
	// dropped-stream problem keeps the generic "problem" wording.
	static labelKey({ state, online }) {
		return state === "problem" && !online ? "offline" : state;
	}

	connect() {
		this.graceElapsed = false;
		this.hadGap = false;
		this.retrying = false;
		// Set true by a successful /up probe when navigator.onLine is stuck false (see
		// attemptReconnect); reset only on a genuine "offline" event, so a later real
		// disconnect is never permanently masked. Narrow exception: a real drop while
		// onLine is already stuck false fires no new "offline" event, so it surfaces as
		// "connecting" rather than "problem" — the safe direction, not a loop. Assumes
		// resync()'s Turbo.visit is a morph refresh (application.html.erb) so this
		// controller instance — and this flag — survive it.
		this.confirmedReachable = false;

		this.graceTimer = setTimeout(() => {
			this.graceElapsed = true;
			this.render();
		}, GRACE_MS);

		this.observer = new MutationObserver(() => this.render());
		this.observer.observe(document.documentElement, {
			subtree: true,
			childList: true,
			attributes: true,
			attributeFilter: ["connected", "data-live-status"],
		});

		this.onlineHandler = (event) => {
			if (event.type === "offline") this.confirmedReachable = false;
			this.render();
		};
		window.addEventListener("online", this.onlineHandler);
		window.addEventListener("offline", this.onlineHandler);

		this.morphAttributeHandler = (event) => {
			if (this.constructor.shouldPreventMorph({ tagName: event.target.tagName, attributeName: event.detail.attributeName })) {
				event.preventDefault();
			}
		};
		document.addEventListener("turbo:before-morph-attribute", this.morphAttributeHandler);

		this.render();
	}

	disconnect() {
		clearTimeout(this.graceTimer);
		this.stopRetrying();
		this.observer?.disconnect();
		window.removeEventListener("online", this.onlineHandler);
		window.removeEventListener("offline", this.onlineHandler);
		document.removeEventListener("turbo:before-morph-attribute", this.morphAttributeHandler);
	}

	// Reads the page, computes state, updates the dot, and auto-resyncs on recovery.
	render() {
		const { sourceCount, connectedCount } = this.constructor.tally(
			[...document.querySelectorAll(SOURCE_SELECTOR)],
			[...document.querySelectorAll(LIVE_SELECTOR)],
		);
		const online = navigator.onLine || this.confirmedReachable;

		let state = this.constructor.computeState({ sourceCount, connectedCount, online });

		// During the grace window, never show red — a fresh page is just "connecting".
		if (state === "problem" && online && !this.graceElapsed) state = "connecting";

		this.paint(state);

		// Remember that a real gap happened so we can auto-resync when it clears.
		if (state === "problem") {
			this.hadGap = true;
			this.startRetrying();
		} else {
			this.stopRetrying();
		}

		// Recovered from a real gap → pull whatever was missed, once.
		if (state === "connected" && this.hadGap) {
			this.hadGap = false;
			this.resync();
		}
	}

	paint(state) {
		this.element.hidden = state === "idle";
		if (state === "idle") return;

		const palette = {
			connected: "bg-verde-500",
			connecting: "bg-dourado-500",
			problem: "bg-carmesim-500",
		};
		// Reset any previously applied colour, then apply the current one.
		this.dotTarget.classList.remove("bg-verde-500", "bg-dourado-500", "bg-carmesim-500");
		this.dotTarget.classList.add(palette[state]);
		if (state === "connecting") this.dotTarget.classList.add("animate-pulse");
		else this.dotTarget.classList.remove("animate-pulse");

		const key = this.constructor.labelKey({ state, online: navigator.onLine });
		const label = this.labelsValue[key] || "";
		this.element.title = label;
		this.element.setAttribute("aria-label", label);
		if (this.hasLabelTarget) this.labelTarget.textContent = label;
	}

	// Backstop for the "online" event being unreliable (mobile, VPNs, captive portals,
	// sleep/wake): while stuck in "problem", periodically probe a cheap health-check
	// endpoint and resync as soon as it succeeds.
	startRetrying() {
		if (this.retrying) return;
		this.retrying = true;
		this.retryTimer = setTimeout(() => this.attemptReconnect(), RETRY_MS);
	}

	stopRetrying() {
		this.retrying = false;
		clearTimeout(this.retryTimer);
	}

	async attemptReconnect() {
		let ok = false;
		try {
			ok = (await fetch("/up", { cache: "no-store" })).ok;
		} catch {
			ok = false;
		}
		if (!this.retrying) return; // state recovered another way while this check was in flight
		if (ok) {
			this.confirmedReachable = true;
			this.hadGap = false;
			this.stopRetrying();
			this.resync();
		} else {
			this.retryTimer = setTimeout(() => this.attemptReconnect(), RETRY_MS);
		}
	}

	// Clicking the dot, or an automatic recovery, re-renders the page from current
	// server state and re-subscribes the stream sources (turbo-rails has no replay).
	resync(event) {
		event?.preventDefault();
		if (document.querySelector(LIVE_SELECTOR)) return;
		Turbo.visit(window.location.href, { action: "replace" });
	}
}
